import { KDrawer } from '@/ui-library/components';
import {
  Button,
  Center,
  Flex,
  Spinner,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Box,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { SearchMd } from '@kula-ai/icons/outline/SearchMd';
import { useRef, useState, useEffect } from 'react';
import { ScheduleForLater } from '../job-stage-actions/actions/assessment/send-assessment/schedule/ScheduleForLater';
import { KAutoComplete } from '@/ui-library/components/organisms/KFields/KAutoComplete';
import { Formik } from 'formik';
import { CustomValueContainer } from './EmailTemplates';
import { useLocalStorageObserver } from '@/hooks/useLocalStorageObserver';

export const EmailTemplateDrawer = ({
  isOpen,
  customIsOpen,
  onClose,
  customOnClose,
  mode,
  handleModalSubmit,
  isLoading,
  isModalCtaLoading,
  modalTitle,
  modalDescription,
  skipTemplateSelection,
  templates,
  selectOnChange,
  renderLeftContent,
  renderRightContent,
  scheduleRef,
  scheduleDetails,
  onSchedule,
  onCancelSchedule,
  isScheduling,
  isScheduleEmail,
  endpointString = '',
  isScheduled = false,
  setIsScheduled = () => {},
  skippedCandidates = [],
  recipients = [],
}) => {
  const userInputRef = useRef();
  const [filteredData, setFilteredData] = useState(templates);
  const [isBulkCtaLoading, setIsBulkCtaLoading] = useState(false);

  // Observe localStorage key "bulkSendEmailIsLoading"
  const bulkSendEmailIsLoading = useLocalStorageObserver(
    'bulkSendEmailIsLoading'
  );

  useEffect(() => {
    setIsBulkCtaLoading(bulkSendEmailIsLoading);
  }, [bulkSendEmailIsLoading]);

  useEffect(() => {
    setFilteredData(templates);
  }, [templates]);

  const renderBody = () => {
    if (filteredData?.length === 0 && mode === 'choose')
      return (
        <Flex
          width="100%"
          alignItems="center"
          justifyContent="center"
          minH="50vh"
        >
          <KText size="xl-medium">No templates found</KText>
        </Flex>
      );

    return (
      <Flex
        w="100%"
        borderTop="1px solid"
        borderColor="border-color"
        minH="50vh"
        flexDir="column"
      >
        {!skipTemplateSelection && renderTemplateSelection()}
        {mode !== 'create' && (
          <Flex
            flexDir="column"
            borderRight="1px solid"
            borderColor="border-color"
            w="35%"
          >
            <Flex w="100%" flexDir="column" maxH="300px" overflowY="auto">
              {filteredData?.map(renderLeftContent)}
            </Flex>
          </Flex>
        )}
        <Flex
          w={mode !== 'create' ? '75%' : '100%'}
          overflowY={mode === 'choose' && 'auto'}
        >
          {renderRightContent()}
        </Flex>
        {isScheduled && isScheduleEmail && (
          <ScheduleForLater
            scheduleRef={scheduleRef}
            scheduleDetails={scheduleDetails}
            onSchedule={onSchedule}
            onCancelSchedule={onCancelSchedule}
            isScheduling={isModalCtaLoading}
            isScheduled={true}
            styles={{
              margin: '8px 16px',
            }}
          />
        )}
      </Flex>
    );
  };

  const renderFooter = () => {
    return (
      <Box borderTop="1px solid" borderColor="border-color">
        <Flex justifyContent="flex-end" gap="6px" p="15px 24px">
          <Button
            variant="secondary"
            data-test-id="secondary-cta-button"
            onClick={() => {
              onClose();
              customOnClose();
            }}
          >
            Cancel
          </Button>
          {!isScheduled && isScheduleEmail && (
            <ScheduleForLater
              scheduleRef={scheduleRef}
              scheduleDetails={scheduleDetails}
              onSchedule={() => {
                onSchedule();
                setIsScheduled(true);
              }}
              onCancelSchedule={onCancelSchedule}
              isScheduling={isModalCtaLoading}
            />
          )}
          <Button
            variant="primary"
            onClick={handleModalSubmit}
            isLoading={isModalCtaLoading || isBulkCtaLoading}
            isDisabled={skippedCandidates?.length > 0 && recipients?.length === 0}
            data-test-id="primary-cta-button"
          >
            {!isScheduled ? 'Send email' : 'Save this email'}
          </Button>
        </Flex>
      </Box>
    );
  };

  const renderTemplateSelection = () => {
    return (
      <Box p="16px 16px 0 16px" mb="-10px">
        {mode === 'create' ? (
          <Formik initialValues={{}} onSubmit={() => {}}>
            <KAutoComplete
              item={{
                name: 'emailTemplates',
                label: 'Email Templates',
                type: 'autoComplete',
                useInput: true,
                isRequired: true,
                isMulti: false,
                placeholder: `Search templates`,
                labelKey: 'name',
                valueKey: 'id',
                customOnChange: selectOnChange,
                options: templates,
                updateEndpointOnChange: false,
                loadDefaultOptions: true,
                fetchConfig: {
                  method: 'get',
                  endPoint: endpointString,
                  processResponse: (res) => {
                    const options = res?.data?.map((item) => ({
                      label: item?.name,
                      value: item?.id,
                      ...item,
                    }));

                    return options;
                  },
                },
                components: {
                  ValueContainer: CustomValueContainer,
                },
              }}
            />
          </Formik>
        ) : (
          <InputGroup w="fit-content">
            <InputLeftElement
              pointerEvents="none"
              children={
                <Icon as={SearchMd} color="secondary-800" width="18" h="100%" />
              }
            />
            <Input
              placeholder={'Search'}
              ref={userInputRef}
              w="250px"
              _placeholder={{
                opacity: 1,
                fontSize: '12px',
                fontWeight: 'medium',
                color: 'secondary-400',
              }}
            />
          </InputGroup>
        )}
      </Box>
    );
  };

  const renderSkippedCandidatesBanner = () => {
    if (!skippedCandidates || skippedCandidates.length === 0) return null;

    const count = skippedCandidates.length;
    const text =
      count === 1
        ? 'Skipped 1 candidate as they don\'t have email address'
        : `Skipped ${count} candidates as they don't have email addresses`;

    return (
      <Box
        bg="pastel-1-light"
        p="12px 16px"
        borderRadius="6px"
        mx="24px"
        mb="16px"
      >
        <KText color="pastel-1-dark" size="sm-medium">
          {text}
        </KText>
      </Box>
    );
  };

  const renderHeader = () => {
    return (
      <Flex flexDir="column" p="16px 24px" w="100%">
        <KText size="md-medium" color="secondary-900">
          {!isScheduled ? 'Send email' : 'Edit email'}
        </KText>
        {modalDescription && (
          <KText fontSize="sm" fontWeight="400" mt="8px">
            {modalDescription}
          </KText>
        )}
      </Flex>
    );
  };

  return (
    <KDrawer
      isOpen={isOpen || customIsOpen}
      placement="right"
      onClose={() => {
        onClose();
        customOnClose();
      }}
      footer={renderFooter()}
      header={renderHeader()}
      contentProps={{
        maxW: '700px',
        borderTopLeftRadius: 'xl',
      }}
    >
      <Flex flexDir="column">
        {renderSkippedCandidatesBanner()}
        {isLoading ? (
          <Center h="100vh" w="100%">
            <Spinner color="primary-800" />
          </Center>
        ) : (
          renderBody()
        )}
      </Flex>
    </KDrawer>
  );
};
